import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import {
  ArticleMetadata,
  BlogSubscribeWidget,
  ParagraphsRenderer,
  RelatedContent,
  SearchMoreLikeThis,
  TeamWidget,
} from '../../../../components';
import { ARTICLE_TYPE_BLOG, RELATED_PROJECTS_HITS_PER_PAGE } from '../../../../config/constants';
import type { NodeArticle } from '../../../../types/uncinc_profile/nodes';
import { profileTeamWidgetMapper } from '../../../../components/Widgets/TeamWidget/profileTeamWidgetMapper';

import './style.scss';

interface NodeArticlePageProps {
  entity: ImmutableEntity<NodeArticle>;
  className?: string;
}

const NodeArticlePage: React.FC<NodeArticlePageProps> = ({ entity, className }) => {
  const { t } = useTranslation();

  const article = entity.toJS();
  const nid = article.nid[0].value;
  const langcode = article.langcode[0].value;
  const articleType = article.field_article_type[0]?.entity.name[0].value;
  const title = article.title[0].value;
  const body = article.body[0]?.processed;
  const image = article.field_media_item[0];
  const caption = image?.entity?.field_caption[0]?.value;
  const publicationDate = article.field_publish_date?.[0]?.value || article.created[0].value;
  const paragraphs = article.field_paragraphs;
  const paragraphsSidebar = article.field_paragraphs_sidebar;
  const departments = article.field_departments;
  const projectTeam = article.field_project_team;
  const projectIDs = article.field_projects.map((project) => project.target_id);
  const themes = article.field_themes;
  const classes = classNames('Node', 'NodeArticlePage', className, {
    [`NodeArticlePage--type--${String(articleType?.replace(' ', '-'))}`]: articleType !== undefined,
  });

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeArticlePage__hero">
        <Container>
          <div className="row">
            <div className="col-12">
              <BreadcrumbsV2 showHomeIcon={false} />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="NodeArticlePage__hero__text-wrapper">
                <div className="NodeArticlePage__hero__text">
                  <div className="row">
                    <h1 className="NodeArticlePage__title col-lg-9" itemProp="title">
                      {title}
                    </h1>
                  </div>

                  <ArticleMetadata className="NodeArticlePage__metadata">
                    {(departments.length > 0 || themes.length > 0) && (
                      <div className="NodeArticlePage__metadata__tags">
                        {departments.length > 0 && (
                          <ArticleMetadata.Department departments={departments} />
                        )}
                        {themes.length > 0 && (
                          <ArticleMetadata.Themes themes={themes} tagsOverflow={false} />
                        )}
                      </div>
                    )}
                    <ArticleMetadata.PublicationDate
                      publicationDate={new Date(publicationDate)}
                      langcode={langcode as unknown as 'nl' | 'en'}
                    />
                  </ArticleMetadata>
                </div>
              </div>
            </div>

          </div>
          {image && (
            <div className="row">
              <div className="col-12">
                <div className="NodeArticlePage__image-wrapper">
                  <div className="NodeArticlePage__image">
                    <DynamicMediaItem
                      media={image}
                      imageAspectRatio="2_1"
                      aboveTheFold
                    />
                  </div>
                  {/* We render the caption outside, to prevent layout shifting */}
                  {caption && (
                    <p className="caption">{caption}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </Container>
      </div>

      <Container>
        <div className="row NodeArticlePage__paragraphs-wrapper">
          <div className="col-12 col-md-7 NodeArticlePage__paragraphs-regular">
            {body && (
              <WysiwygField
                className="NodeArticlePage__body body--large"
                property="schema:text"
                html={body}
              />
            )}

            <ParagraphsRenderer className="NodeArticlePage__paragraphs" paragraphs={paragraphs} noBootstrap />
          </div>
          <div
            role="complementary"
            aria-label={t('general.related_info')}
            className="col-12 col-md-5 col-lg-4 offset-lg-1 NodeArticlePage__paragraphs-sidebar"
          >
            {projectTeam.length > 0 && (
              <TeamWidget
                context="article"
                members={profileTeamWidgetMapper(projectTeam)}
              />
            )}

            {articleType && Object.values(ARTICLE_TYPE_BLOG).includes(articleType) && (
              <BlogSubscribeWidget />
            )}

            <ParagraphsRenderer className="NodeArticlePage__paragraphs" paragraphs={paragraphsSidebar} noBootstrap />
          </div>
        </div>
      </Container>

      <div className="FixedReferenceBlocks">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10">
              <div className="ProfilePage__projects">
                <RelatedContent
                  title={t('content_types.project.related_content')}
                  contentType="project"
                  hitsPerPage={RELATED_PROJECTS_HITS_PER_PAGE}
                  filterNames={['nid']}
                  filterValues={projectIDs}
                  langcode={langcode}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>

      <div className="NodeArticlePage__related-content">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10">
              <SearchMoreLikeThis
                entityId={nid}
                langcode={langcode}
                contentType="article"
                title={t('content_types.article.related_content')}
              />
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default NodeArticlePage;
